import React, { useState, useRef, useEffect } from 'react';
import { FileUpload } from './components/FileUpload';
import { GrowthRateInput } from './components/GrowthRateInput';
import { ForecastTabs } from './components/ForecastTabs';
import { HelpModal } from './components/HelpModal';
import { SalesData, ForecastData } from './types';
import { parseSalesData, generateForecasts, loadDefaultSalesData } from './utils/forecastUtils';
import { Login } from './components/Login';
import './App.css';
import { LoadingProvider } from './context/LoadingContext';
import LoadingIndicator from './components/LoadingIndicator';

function App() {
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [growthRate, setGrowthRate] = useState<number>(3); // Set a default growth rate
  const [forecasts, setForecasts] = useState<ForecastData | null>(null);
  const [loading, setLoading] = useState(false);
  const forecastResultsRef = useRef<HTMLDivElement>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Load default data for demo
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await loadDefaultSalesData();
        if (data && data.length > 0) {
          setSalesData(data);
          // Automatically generate initial forecast
          const initialForecasts = generateForecasts(data, growthRate);
          setForecasts(initialForecasts);
        }
      } catch (error) {
        console.error('Error loading default data:', error);
      }
      setLoading(false);
    };
    loadData();
  }, [growthRate]);

  const handleFileUpload = async (file: File) => {
    setLoading(true);
    try {
      const data = await parseSalesData(file);
      setSalesData(data);
    } catch (error) {
      console.error('Error parsing file:', error);
      alert('Error parsing file. Please check the format and try again.');
    }
    setLoading(false);
  };

  const handleForecast = () => {
    if (!salesData) return;
    setLoading(true);
    try {
      // Always use flat growth rate calculation
      const forecasts = generateForecasts(salesData, growthRate);
      setForecasts(forecasts);
      // Scroll to forecast section after a small delay to ensure rendering
      setTimeout(() => {
        forecastResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      console.error('Error generating forecast:', error);
      alert('Error generating forecast. Please try again.');
    }
    setLoading(false);
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  return (
    <LoadingProvider>
      <LoadingIndicator />
      <div className="app">
        {!isAuthenticated ? (
          <Login onLogin={handleLogin} />
        ) : (
          <>
            <header className="app-header">
              <h1>Sales Forecasting Tool</h1>
              <p>Amazon + D2C Compatible<br />
             <em> This tool was adapted off of the fantastic excel tool created by renowned Dani Krichbaum, Chief Operations Officer of Beauty by Earth.
              All credit to Dani for the original tool and the inspiration for this project.</em></p>
              <button 
                className="help-button"
                onClick={() => setShowHelp(true)}
                aria-label="Open help documentation"
              >
                ?
              </button>
            </header>

            <main className="app-main">
              <div className="upload-section">
                <FileUpload onFileUpload={handleFileUpload} loading={loading} />
                {salesData && (
                  <>
                    <GrowthRateInput 
                      value={growthRate} 
                      onChange={setGrowthRate} 
                    />
                    <button 
                      className="forecast-button"
                      onClick={handleForecast}
                      disabled={loading}
                    >
                      {loading ? 'Doing some fancy forecasting...' : 'Forecast Now'}
                    </button>
                  </>
                )}
              </div>
              <div ref={forecastResultsRef}>
                {forecasts && (
                  <ForecastTabs 
                    forecasts={forecasts} 
                    salesData={salesData} 
                    defaultSalesData={salesData} 
                  />
                )}
              </div>
            </main>
            <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
            <footer className="app-footer">
              <small>Made with 🤍 by Jenny</small>
            </footer>
          </>
        )}
      </div>
    </LoadingProvider>
  );
}

export default App; 